import { useRef } from "react"
import { Employee } from "types/Employee"
import {
  CarrotPlanFormOption,
  MenuItemOptionFields,
  TopicOptionFields,
  useCarrotPlanMappings
} from "./useCarrotPlanMappings"
import { NONE_VALUE } from "../JourneySelection"

type UseDynamicCarrotPlanMappingsArgs = {
  employee: Employee
  initialMenuItems: CarrotPlanFormOption<MenuItemOptionFields>[]
  initialTopics: CarrotPlanFormOption<TopicOptionFields>[]
  journey: number
  stage: number
}

type UseDynamicCarrotPlanMappingsResult = {
  menuItems: CarrotPlanFormOption<MenuItemOptionFields>[]
  topics: CarrotPlanFormOption<TopicOptionFields>[]
  isLoading: boolean
}

/**
 * Resolves the menu items + topics to display for the current (journey, stage):
 * the parent-supplied initials when the user hasn't moved off the initial selection,
 * otherwise a fresh fetch for the new selection. `isLoading` only reflects the fetch path.
 */
export function useDynamicCarrotPlanMappings({
  employee,
  initialMenuItems,
  initialTopics,
  journey,
  stage
}: UseDynamicCarrotPlanMappingsArgs): UseDynamicCarrotPlanMappingsResult {
  const initialSelectionRef = useRef({ journey, stage })

  const hasJourneySelectionChanged =
    journey !== initialSelectionRef.current.journey || stage !== initialSelectionRef.current.stage
  const bothJourneyAndStageSelected = journey !== NONE_VALUE && stage !== NONE_VALUE
  const shouldFetchNewOptions = hasJourneySelectionChanged && bothJourneyAndStageSelected

  const {
    mappedFormMenuItems: fetchedMenuItems,
    mappedFormTopics: fetchedTopics,
    areMappingsLoading
  } = useCarrotPlanMappings(
    employee,
    shouldFetchNewOptions ? journey : undefined,
    shouldFetchNewOptions ? stage : undefined
  )

  return {
    menuItems: shouldFetchNewOptions ? fetchedMenuItems : initialMenuItems,
    topics: shouldFetchNewOptions ? fetchedTopics : initialTopics,
    isLoading: shouldFetchNewOptions && areMappingsLoading
  }
}
